/**
 * /partners — Sponsor-brand management. Companies that pay JackTrack
 * and supply product for the bus-tasting events.
 *
 * Lifecycle: pending → working → active → archived
 *  - Sales submit → pending (visible only to that rep + admins)
 *  - Admin approves → working (amber badge; DocuSign in flight)
 *  - Admin manually activates → active (green badge; contract signed)
 *  - Admin archives when partnership ends
 *
 * Visibility: sales reps see only what THEY submitted. Admins +
 * dispatchers see everything. (Per user choice 10A; may flip to 10B
 * "everyone sees all active partners" later.)
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Handshake,
  CheckCircle2,
  Clock,
  Archive,
  AlertTriangle,
  ExternalLink,
  Mail,
  Phone,
  Globe,
  Instagram,
  Facebook,
  Twitter,
  Linkedin,
  Music,
  Trash2,
  Loader2,
  Search,
  RotateCcw,
  XCircle,
  Pencil,
  FileSignature,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthProvider";
import {
  fetchPartners,
  createPartner,
  approvePartner,
  rejectPartner,
  activatePartner,
  archivePartner,
  unarchivePartner,
  deletePartner,
  PARTNER_BRAND_PALETTE,
  partnerInitials,
} from "../lib/api";
import PartnerPriorityDrawer from "../components/PartnerPriorityDrawer";
import { useSearchParams } from "react-router-dom";

const PARTNER_TYPES = [
  { value: "hardware", label: "Hardware (vape, batteries, accessories)" },
  { value: "brand_flower", label: "Cannabis — flower" },
  { value: "brand_concentrates", label: "Cannabis — concentrates / extracts" },
  { value: "brand_edibles", label: "Cannabis — edibles" },
  { value: "brand_prerolls", label: "Cannabis — pre-rolls" },
  { value: "brand_other", label: "Cannabis — other" },
  { value: "merch", label: "Merchandise / apparel" },
  { value: "service", label: "Service provider" },
  { value: "other", label: "Other" },
];
const TYPE_LABEL = Object.fromEntries(PARTNER_TYPES.map((t) => [t.value, t.label]));

const STATUS_META = {
  pending: { label: "Pending", color: "bg-zinc-500/15 border-zinc-500/30 text-zinc-300", icon: Clock },
  working: { label: "Working", color: "bg-amber-500/15 border-amber-500/30 text-amber-300", icon: FileSignature },
  active: { label: "Active", color: "bg-emerald-500/15 border-emerald-500/30 text-emerald-300", icon: CheckCircle2 },
  archived: { label: "Archived", color: "bg-zinc-700/40 border-zinc-700 text-zinc-500", icon: Archive },
};

const SOCIAL_ICONS = {
  instagram: Instagram,
  facebook: Facebook,
  twitter: Twitter,
  linkedin: Linkedin,
  tiktok: Music,
  other: Globe,
};

export default function Partners() {
  const { isAdmin, isDispatcher } = useAuth();
  const isStaffView = isAdmin || isDispatcher;
  const canWritePriorities = isAdmin || isDispatcher;
  const [partners, setPartners] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [priorityPartner, setPriorityPartner] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    try {
      const rows = await fetchPartners();
      setPartners(rows);
    } catch (err) {
      toast.error("Could not load partners", { description: err?.message });
      setPartners([]);
    }
  };
  useEffect(() => {
    load();
  }, []);

  // Deep-link: `?focus=<partner_id>` (from the dispensary chip stack)
  // opens the priority drawer for that partner as soon as the list loads.
  useEffect(() => {
    const focusId = searchParams.get("focus");
    if (!focusId || !partners) return;
    const target = partners.find((p) => p.id === focusId);
    if (target) {
      setPriorityPartner(target);
      // Clean the URL so a refresh doesn't keep auto-opening.
      searchParams.delete("focus");
      setSearchParams(searchParams, { replace: true });
    }
  }, [partners, searchParams, setSearchParams]);

  const filtered = useMemo(() => {
    if (!partners) return [];
    return partners.filter((p) => {
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !p.name.toLowerCase().includes(q) &&
          !(p.products || "").toLowerCase().includes(q) &&
          !(p.contact_name || "").toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
  }, [partners, statusFilter, search]);

  const pending = useMemo(
    () => (partners || []).filter((p) => p.status === "pending"),
    [partners]
  );

  const counts = useMemo(() => {
    const c = { all: 0, pending: 0, working: 0, active: 0, archived: 0 };
    (partners || []).forEach((p) => {
      c.all += 1;
      c[p.status] = (c[p.status] || 0) + 1;
    });
    return c;
  }, [partners]);

  return (
    <div data-testid="partners-page" className="flex flex-col gap-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-50 flex items-center gap-2">
            <Handshake className="w-5 h-5 text-emerald-400" />
            Partners
          </h1>
          <p className="text-xs text-zinc-500 max-w-xl leading-relaxed">
            Sponsor brands that pay us and supply product for bus events.
            {isStaffView
              ? " You see every submission across the team."
              : " You see your own submissions plus every active partner across the team."}
          </p>
        </div>
        <Button
          data-testid="new-partner-btn"
          onClick={() => setCreating(true)}
          className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          New partner
        </Button>
      </div>

      {/* Admin: Pending Approvals strip */}
      {isStaffView && pending.length > 0 && (
        <div
          data-testid="pending-approvals-strip"
          className="panel p-4 border border-amber-500/30 bg-amber-500/5"
        >
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <h3 className="text-sm font-semibold text-amber-200">
              {pending.length} partner{pending.length === 1 ? "" : "s"} awaiting your approval
            </h3>
          </div>
          <ul className="flex flex-col gap-2">
            {pending.map((p) => (
              <PendingApprovalRow key={p.id} partner={p} onChange={load} />
            ))}
          </ul>
        </div>
      )}

      {/* Toolbar */}
      <div className="panel p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            data-testid="partner-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, product or contact…"
            className="pl-8 bg-zinc-900 border-zinc-800 text-zinc-100 h-9"
          />
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {["all", "working", "active", "pending", "archived"].map((s) => (
            <button
              key={s}
              data-testid={`partner-filter-${s}`}
              onClick={() => setStatusFilter(s)}
              className={`px-2.5 py-1 rounded text-[11px] font-mono uppercase tracking-wider border transition-colors ${
                statusFilter === s
                  ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                  : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:bg-zinc-800"
              }`}
            >
              {s} {counts[s] ? `(${counts[s]})` : ""}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {partners === null ? (
        <div className="text-xs text-zinc-500 font-mono px-2">Loading partners…</div>
      ) : filtered.length === 0 ? (
        <div
          data-testid="partners-empty"
          className="panel p-8 text-center text-sm text-zinc-500"
        >
          {(partners || []).length === 0
            ? "No partners yet. Submit your first using New partner."
            : "No partners match these filters."}
        </div>
      ) : (
        <div
          data-testid="partners-grid"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
        >
          {filtered.map((p) => (
            <PartnerCard
              key={p.id}
              partner={p}
              isAdmin={isAdmin}
              onChange={load}
              onOpenPriorities={() => setPriorityPartner(p)}
            />
          ))}
        </div>
      )}

      {creating && (
        <NewPartnerDialog
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            load();
          }}
        />
      )}

      {priorityPartner && (
        <PartnerPriorityDrawer
          partner={priorityPartner}
          canWrite={canWritePriorities}
          onClose={() => setPriorityPartner(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pending-approval row (admin-only, top of page when pending exist)
// ---------------------------------------------------------------------------
function PendingApprovalRow({ partner, onChange }) {
  const [busy, setBusy] = useState(false);
  const approve = async () => {
    setBusy(true);
    try {
      await approvePartner(partner.id);
      toast.success(`Approved ${partner.name}`, {
        description: "Status set to Working. Sales rep can see it now.",
      });
      onChange();
    } catch (err) {
      toast.error("Could not approve", {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setBusy(false);
    }
  };
  const reject = async () => {
    const reason = window.prompt(
      `Reject "${partner.name}"? (optional reason for audit log)`,
      ""
    );
    if (reason === null) return;
    setBusy(true);
    try {
      await rejectPartner(partner.id, reason || undefined);
      toast.success(`Rejected ${partner.name}`);
      onChange();
    } catch (err) {
      toast.error("Could not reject", {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setBusy(false);
    }
  };
  return (
    <li
      data-testid={`pending-row-${partner.id}`}
      className="bg-zinc-950/60 border border-zinc-800 rounded px-3 py-2 flex items-center gap-3 flex-wrap"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-zinc-100">{partner.name}</span>
          <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 border border-zinc-700">
            {TYPE_LABEL[partner.partner_type] || partner.partner_type}
          </span>
        </div>
        <div className="text-xs text-zinc-500 mt-0.5 truncate">
          {partner.products}
          {partner.submitted_by_name ? ` · submitted by ${partner.submitted_by_name}` : ""}
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <Button
          data-testid={`approve-${partner.id}`}
          size="sm"
          onClick={approve}
          disabled={busy}
          className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50 h-8"
        >
          <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
          Approve
        </Button>
        <Button
          data-testid={`reject-${partner.id}`}
          size="sm"
          onClick={reject}
          disabled={busy}
          variant="outline"
          className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 h-8"
        >
          <XCircle className="w-3.5 h-3.5 mr-1.5" />
          Reject
        </Button>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Partner card
// ---------------------------------------------------------------------------
function PartnerCard({ partner, isAdmin, onChange, onOpenPriorities }) {
  const status = STATUS_META[partner.status] || STATUS_META.pending;
  const StatusIcon = status.icon;
  const [busy, setBusy] = useState(false);
  const brandColor = partner.brand_color || "#64748b";

  const doAction = async (fn, label) => {
    setBusy(true);
    try {
      await fn();
      toast.success(label);
      onChange();
    } catch (err) {
      toast.error("Could not update", {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setBusy(false);
    }
  };

  const socials = partner.socials || {};
  const socialEntries = Object.entries(socials).filter(([_k, v]) => v);

  return (
    <div
      data-testid={`partner-card-${partner.id}`}
      className="panel p-4 flex flex-col gap-3 border-l-4"
      style={{ borderLeftColor: brandColor }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <span
            data-testid={`partner-brand-chip-${partner.id}`}
            className="inline-flex items-center justify-center w-7 h-7 rounded-full text-[11px] font-mono font-bold shrink-0 shadow-sm"
            style={{ background: brandColor, color: "#0f172a" }}
            title={partner.name}
          >
            {partnerInitials(partner.name)}
          </span>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-zinc-50 truncate">{partner.name}</h3>
            <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mt-0.5">
              {TYPE_LABEL[partner.partner_type] || partner.partner_type}
            </div>
          </div>
        </div>
        <span
          data-testid={`partner-status-${partner.id}`}
          className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${status.color} shrink-0`}
        >
          <StatusIcon className="w-3 h-3" />
          {status.label}
        </span>
      </div>

      <div className="text-xs text-zinc-300 leading-relaxed">
        {partner.products}
      </div>

      {/* Contact */}
      <div className="flex flex-col gap-1 text-xs">
        {partner.contact_name && (
          <div className="text-zinc-400">
            <span className="text-[10px] uppercase font-mono text-zinc-500 mr-1">Contact:</span>
            {partner.contact_name}
          </div>
        )}
        {partner.contact_phone && (
          <a
            href={`tel:${partner.contact_phone}`}
            className="inline-flex items-center gap-1.5 text-zinc-300 hover:text-emerald-300"
          >
            <Phone className="w-3 h-3" />
            {partner.contact_phone}
          </a>
        )}
        {partner.contact_email && (
          <a
            href={`mailto:${partner.contact_email}`}
            className="inline-flex items-center gap-1.5 text-zinc-300 hover:text-emerald-300 truncate"
          >
            <Mail className="w-3 h-3 shrink-0" />
            {partner.contact_email}
          </a>
        )}
        {partner.website && (
          <a
            href={partner.website.startsWith("http") ? partner.website : `https://${partner.website}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-emerald-300 hover:text-emerald-200 truncate"
          >
            <Globe className="w-3 h-3 shrink-0" />
            {partner.website.replace(/^https?:\/\//, "")}
            <ExternalLink className="w-2.5 h-2.5 shrink-0" />
          </a>
        )}
      </div>

      {/* Socials */}
      {socialEntries.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {socialEntries.map(([net, val]) => {
            const Icon = SOCIAL_ICONS[net] || Globe;
            return (
              <span
                key={net}
                className="inline-flex items-center gap-1 text-[10px] font-mono text-zinc-400 bg-zinc-900 border border-zinc-800 rounded px-1.5 py-0.5"
                title={`${net}: ${val}`}
              >
                <Icon className="w-2.5 h-2.5" />
                {val}
              </span>
            );
          })}
        </div>
      )}

      {partner.notes && (
        <div className="text-xs text-zinc-400 italic bg-zinc-900/50 border-l-2 border-zinc-700 pl-2 py-1">
          {partner.notes}
        </div>
      )}

      <div className="text-[10px] font-mono text-zinc-600 pt-1 border-t border-zinc-800 mt-auto">
        Submitted by {partner.submitted_by_name || partner.submitted_by_email}
      </div>

      <Button
        data-testid={`view-priorities-${partner.id}`}
        size="sm"
        variant="outline"
        onClick={onOpenPriorities}
        className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-200 h-8 text-xs justify-start"
      >
        <FileSignature className="w-3.5 h-3.5 mr-1.5" style={{ color: brandColor }} />
        Priority dispensaries
      </Button>

      {/* Admin action buttons */}
      {isAdmin && partner.status !== "pending" && (
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          {partner.status === "working" && (
            <Button
              data-testid={`activate-${partner.id}`}
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() =>
                doAction(() => activatePartner(partner.id), `Activated ${partner.name}`)
              }
              className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 h-7 text-xs"
            >
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Mark active
            </Button>
          )}
          {(partner.status === "working" || partner.status === "active") && (
            <Button
              data-testid={`archive-${partner.id}`}
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() =>
                doAction(() => archivePartner(partner.id), `Archived ${partner.name}`)
              }
              className="border-zinc-700 text-zinc-400 hover:bg-zinc-800 h-7 text-xs"
            >
              <Archive className="w-3 h-3 mr-1" />
              Archive
            </Button>
          )}
          {partner.status === "archived" && (
            <Button
              data-testid={`unarchive-${partner.id}`}
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() =>
                doAction(() => unarchivePartner(partner.id), `Restored ${partner.name}`)
              }
              className="border-zinc-700 text-zinc-400 hover:bg-zinc-800 h-7 text-xs"
            >
              <RotateCcw className="w-3 h-3 mr-1" />
              Restore
            </Button>
          )}
          <Button
            data-testid={`delete-${partner.id}`}
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => {
              if (
                window.confirm(
                  `Permanently delete partner ${partner.name}? This cannot be undone.`
                )
              ) {
                doAction(() => deletePartner(partner.id), `Deleted ${partner.name}`);
              }
            }}
            className="text-zinc-500 hover:text-rose-300 hover:bg-rose-500/10 h-7 text-xs ml-auto"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// New-partner dialog (anyone can open; sales submits to pending, admin
// submission lands straight in working — handled server-side).
// ---------------------------------------------------------------------------
function NewPartnerDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    partner_type: "brand_flower",
    products: "",
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    website: "",
    notes: "",
    instagram: "",
    facebook: "",
    twitter: "",
    tiktok: "",
    brand_color: PARTNER_BRAND_PALETTE[0].hex,
  });
  const [saving, setSaving] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const canSubmit =
    form.name.trim() &&
    form.products.trim() &&
    (form.contact_phone.trim() || form.contact_email.trim());

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) {
      toast.error("Name, products and at least one contact method are required.");
      return;
    }
    setSaving(true);
    try {
      const socials = {};
      ["instagram", "facebook", "twitter", "tiktok"].forEach((k) => {
        if (form[k].trim()) socials[k] = form[k].trim();
      });
      await createPartner({
        name: form.name.trim(),
        partner_type: form.partner_type,
        products: form.products.trim(),
        contact_name: form.contact_name.trim() || null,
        contact_phone: form.contact_phone.trim() || null,
        contact_email: form.contact_email.trim() || null,
        website: form.website.trim() || null,
        notes: form.notes.trim() || null,
        socials: Object.keys(socials).length ? socials : null,
        brand_color: form.brand_color,
      });
      toast.success("Submitted for approval", {
        description: "An admin will review and either approve or reject.",
      });
      onCreated();
    } catch (err) {
      toast.error("Could not submit", {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="new-partner-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-zinc-50">Submit a new partner</DialogTitle>
          <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
            Fill out who they are and how to reach them. An admin will review,
            approve, and (once the DocuSign contract comes back) mark them
            Active.
          </p>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3 mt-2">
          {/* Identity */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400">
                Company name <span className="text-rose-400">*</span>
              </Label>
              <Input
                data-testid="partner-name"
                value={form.name}
                onChange={onChange("name")}
                placeholder="Acme Vapes"
                className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400">
                Partner type <span className="text-rose-400">*</span>
              </Label>
              <Select
                value={form.partner_type}
                onValueChange={(v) => setForm({ ...form, partner_type: v })}
              >
                <SelectTrigger
                  data-testid="partner-type"
                  className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  {PARTNER_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400">
              Brand color
            </Label>
            <p className="text-[10px] text-zinc-600 mt-0.5">
              Shown as a chip on priority dispensaries so reps can
              recognize this partner at a glance.
            </p>
            <div
              data-testid="brand-color-palette"
              className="flex items-center gap-2 flex-wrap mt-2"
            >
              {PARTNER_BRAND_PALETTE.map((c) => (
                <button
                  key={c.hex}
                  type="button"
                  data-testid={`brand-color-${c.hex}`}
                  onClick={() => setForm({ ...form, brand_color: c.hex })}
                  title={c.name}
                  className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${
                    form.brand_color === c.hex
                      ? "border-zinc-100 scale-110"
                      : "border-zinc-800"
                  }`}
                  style={{ background: c.hex }}
                />
              ))}
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400">
              What do they make / sell <span className="text-rose-400">*</span>
            </Label>
            <Textarea
              data-testid="partner-products"
              value={form.products}
              onChange={onChange("products")}
              placeholder="vape pens, batteries, gummies, infused pre-rolls…"
              rows={2}
              className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 resize-none"
            />
          </div>

          {/* Contact */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400">
                Contact name
              </Label>
              <Input
                data-testid="partner-contact-name"
                value={form.contact_name}
                onChange={onChange("contact_name")}
                placeholder="Jane Smith"
                className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400">
                Phone <span className="text-zinc-600">(or email)</span>
              </Label>
              <Input
                data-testid="partner-contact-phone"
                value={form.contact_phone}
                onChange={onChange("contact_phone")}
                placeholder="(555) 123-4567"
                className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400">
                Email <span className="text-zinc-600">(or phone)</span>
              </Label>
              <Input
                data-testid="partner-contact-email"
                value={form.contact_email}
                onChange={onChange("contact_email")}
                type="email"
                placeholder="jane@acme.com"
                className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1"
              />
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400">
              Website
            </Label>
            <Input
              data-testid="partner-website"
              value={form.website}
              onChange={onChange("website")}
              placeholder="https://acme.com"
              className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1"
            />
          </div>

          {/* Socials (optional) */}
          <details className="pt-1">
            <summary className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 cursor-pointer hover:text-zinc-300 select-none">
              Social media (optional)
            </summary>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
              {["instagram", "facebook", "twitter", "tiktok"].map((net) => (
                <div key={net}>
                  <Label className="text-[10px] font-mono text-zinc-500 capitalize">
                    {net}
                  </Label>
                  <Input
                    data-testid={`partner-social-${net}`}
                    value={form[net]}
                    onChange={onChange(net)}
                    placeholder={net === "instagram" ? "@acmevapes" : ""}
                    className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 h-8 text-sm"
                  />
                </div>
              ))}
            </div>
          </details>

          <div>
            <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400">
              Notes for the admin reviewer
            </Label>
            <Textarea
              data-testid="partner-notes"
              value={form.notes}
              onChange={onChange("notes")}
              placeholder="Anything the admin should know before approving (sales pitch, intro details, contract terms discussed)…"
              rows={2}
              className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 resize-none"
            />
          </div>

          <DialogFooter className="gap-2 mt-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-100"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="submit-partner"
              disabled={!canSubmit || saving}
              className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-emerald-50"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-1.5" />
                  Submit for approval
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
